import * as React from 'react';

import * as ramda from 'ramda';
import _ from 'underscore';

import type {SavedFilter} from 'nutshell-core/types';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {HairlineBorder} from 'shells/layout/hairline-border';
import {ListViewScrollable} from 'shells/list-view/list-view-scrollable';
import {Modal} from 'shells/modal/modal';
import {Radio} from 'shells/radio/radio';
import {RadioGroupSlider} from 'shells/radio/radio-group-slider';
import {SearchBar} from 'shells/search-bar/search-bar';

import styles from './saved-filter-modal.module.css';

// @ts-expect-error
const getCachedSearchPattern = _.memoize((searchValue) => new RegExp(searchValue, 'i'));

export const SHARE_TYPE_JUST_MINE = 'mine';
export const SHARE_TYPE_SHOW_ALL = 'shared';

export type ShareTypeJustMine = 'mine';
export type ShareTypeShowAll = 'shared';

type Props = {
    headerText: string;
    searchPlaceholder: string;
    preselectedTab?: ShareTypeJustMine | ShareTypeShowAll;
    isOpen: boolean;
    onClose: () => any;
    savedFilters: SavedFilter<any>[];
    sessionUserId: string | null | undefined;
    isLoading: boolean;
    isErrored: boolean;
    errorMessage?: string;
    getExtraSearchableText?: (arg1: SavedFilter<any>) => string;
    onErrorHidden: () => any;
    renderFilterRow: (arg1: any, arg2: boolean) => React.ReactElement<any>;
    renderEmptyView: () => React.ReactElement<any>;
    renderNoPersonalSavedFilters: () => React.ReactElement<any>;
};

type ShareType = ShareTypeJustMine | ShareTypeShowAll;

type State = {
    searchVal: string;
    shareTypeFilter: ShareType;
};

export class SavedFilterModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            searchVal: '',
            shareTypeFilter: props.preselectedTab || SHARE_TYPE_SHOW_ALL,
        };
    }

    render() {
        // Our list view expects our items to be wrapped in an object
        const filteredRows = this.getFilteredRows(this.props.savedFilters).map((item) => {
            return {
                rowItem: item,
            };
        });

        return (
            <Modal
                isOpen={this.props.isOpen}
                headerText={this.props.headerText}
                onClose={this.props.onClose}
            >
                <div className={styles.container}>
                    <div className={styles['saved-filter-row--header']}>
                        <SearchBar
                            value={this.state.searchVal}
                            onChange={this.handleSearchValueChange}
                            isClearable={true}
                            placeholder={this.props.searchPlaceholder}
                        />
                        <RadioGroupSlider
                            name='sharedFilter'
                            size='big'
                            selectedValue={this.state.shareTypeFilter}
                            selectedIndex={
                                this.state.shareTypeFilter === SHARE_TYPE_SHOW_ALL ? 0 : 1
                            }
                            // @ts-expect-error
                            onChange={this.handleToggleShareTypeFilter}
                        >
                            <Radio value={SHARE_TYPE_SHOW_ALL}>Show all</Radio>
                            <Radio value={SHARE_TYPE_JUST_MINE}>Just mine</Radio>
                        </RadioGroupSlider>
                    </div>
                    <HairlineBorder insetPixels={16} />
                    <div className={styles['saved-filter-list']}>
                        <ListViewScrollable
                            collection={filteredRows}
                            renderRow={this.props.renderFilterRow}
                            shouldfadeBottom={true}
                            specialStates={[
                                {
                                    shouldRender: this.shouldRenderNoPersonalSavedFilters,
                                    component: this.props.renderNoPersonalSavedFilters(),
                                },
                                {
                                    shouldRender: () => {
                                        return this.shouldRenderEmpty(filteredRows);
                                    },
                                    component: this.props.renderEmptyView(),
                                },
                            ]}
                        />
                    </div>
                </div>
                {this.props.errorMessage && (
                    <FlashBanner
                        type='error'
                        duration={3500}
                        className={styles['flash-banner']}
                        onHidden={this.props.onErrorHidden}
                    >
                        {this.props.errorMessage}
                    </FlashBanner>
                )}
            </Modal>
        );
    }

    isOwnFilter = (filter: SavedFilter<any>) => {
        return (
            // Default filters are not specifically the users
            !filter.isImmutable && filter.links.user === this.props.sessionUserId
        ); // Or shared filters that belong to them
    };

    shouldRenderNoPersonalSavedFilters = () => {
        return (
            this.state.shareTypeFilter === SHARE_TYPE_JUST_MINE &&
            ramda.none(this.isOwnFilter, this.props.savedFilters)
        );
    };

    shouldRenderEmpty = (filteredRows: any[]) => {
        return (
            this.props.isLoading ||
            this.props.isErrored ||
            !this.props.savedFilters.length ||
            !filteredRows.length
        );
    };

    getFilteredRows(savedFilters: SavedFilter<any>[]) {
        let filteredRows = savedFilters;

        // Default is to just show _all_ filters
        if (this.state.shareTypeFilter === SHARE_TYPE_JUST_MINE) {
            filteredRows = filteredRows.filter(this.isOwnFilter);
        }

        // No search text, just return our filteredRows
        if (!this.state.searchVal.length) {
            return filteredRows;
        }

        const pattern = getCachedSearchPattern(this.state.searchVal);

        return filteredRows.filter((row) => {
            if (typeof this.props.getExtraSearchableText === 'function') {
                const extraSearchableText = this.props.getExtraSearchableText(row);

                return pattern.test(row.name) || pattern.test(extraSearchableText);
            }

            return pattern.test(row.name);
        });
    }

    handleSearchValueChange = (val: string) => {
        this.setState({searchVal: val});
    };

    handleToggleShareTypeFilter = (val: ShareType) => {
        this.setState({shareTypeFilter: val});
    };
}
